/**
 * Build-with-AI panel (S9) — turns an accepted proposal + its preview result
 * into a full, valid `DashboardTile`. The panel adds tiles directly (no
 * follow-up config step like the Add Tile dialog), so it must synthesize a
 * complete, sensible config itself — reusing the SAME `shapeSuggest` autopilot
 * the dialog uses, so a chart/pivot proposal only ever gets a config the data
 * actually supports.
 */
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
import type {
  DashboardEngineId,
  DashboardTile,
  DashboardTileType,
  NoteTile,
  TileLayout,
} from '../types/dashboardTypes';
import { shapeSuggest, type ShapeSuggestions, type TileSuggestion } from '../utils/shapeSuggest';
import type { TileProposal } from './aiTileProposal';

/**
 * Honors the AI's suggested tile type only if the actual result shape
 * supports it (present among the autopilot's best/alternatives); otherwise
 * falls back to the autopilot's best pick for the real data — prevents
 * building an invalid tile (e.g. a pivot with no second category column)
 * from a mismatched AI guess. `wanted` spans every query-backed type (the
 * starter path passes template `defaultTileType`s), a superset of what the
 * AI can propose.
 */
export function pickSuggestionForProposal(
  suggestions: ShapeSuggestions,
  wanted: Exclude<DashboardTileType, 'note'>,
): TileSuggestion {
  if (suggestions.best.tileType === wanted) return suggestions.best;
  const alt = suggestions.alternatives.find((s) => s.tileType === wanted);
  return alt ?? suggestions.best;
}

export function shapeSuggestionsForProposal(
  columns: QueryColumn[],
  rows: Array<Record<string, unknown>>,
): ShapeSuggestions {
  return shapeSuggest({ columns, rows });
}

const DEFAULT_CHART_CONFIG: ChartConfig = {
  type: 'bar',
  xAxis: { column: '' },
  yAxis: [],
  showLegend: false,
  showGrid: true,
};

export function buildTileFromSuggestion(args: {
  /** Only title + sql are consumed — S20's pin path reuses this builder. */
  proposal: TileProposal;
  suggestion: TileSuggestion;
  connectionId: string;
  database?: string;
  engine?: DashboardEngineId;
  layout: TileLayout;
}): Exclude<DashboardTile, NoteTile> {
  const { proposal, suggestion, connectionId, database, engine, layout } = args;
  const id = crypto.randomUUID();
  const title = proposal.title || `New ${suggestion.tileType}`;
  const base = {
    id,
    title,
    layout,
    connectionId,
    database,
    engine,
    source: { kind: 'sql' as const, sql: proposal.sql },
    refresh: { mode: 'manual' as const },
  };

  switch (suggestion.tileType) {
    case 'chart':
      return { ...base, tileType: 'chart', chartConfig: suggestion.chartConfig ?? DEFAULT_CHART_CONFIG };
    case 'stat':
      return {
        ...base,
        tileType: 'stat',
        valueColumn: suggestion.valueColumn,
        format: { kind: 'number' },
        compare: { kind: 'none' },
        sparkline: suggestion.sparkline,
      };
    case 'pivot':
      return {
        ...base,
        tileType: 'pivot',
        rowColumn: suggestion.rowColumn,
        columnColumn: suggestion.columnColumn,
        valueColumn: suggestion.valueColumn,
        aggregation: 'sum',
      };
    case 'map':
      return { ...base, tileType: 'map', geometryColumn: suggestion.geometryColumn };
    case 'table':
    default:
      return { ...base, tileType: 'table', rowLimit: 1000, density: 'comfortable' };
  }
}
